import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { HeartPulse, AlertTriangle, RefreshCw, TrendingUp, PauseCircle, CheckCircle2, Users } from 'lucide-react';

const REC_ICON = {
  recover: RefreshCw, renew: RefreshCw, risk: AlertTriangle,
  grow: TrendingUp, suspend: PauseCircle, ok: CheckCircle2,
};
const COLOR = {
  green: '#10b981', blue: '#3b82f6', yellow: '#f59e0b', red: '#ef4444',
};

export default function BaseHealthReport({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get('/stats/base-health')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  if (loading) return <Card><CardContent className="p-6 text-sm text-muted-foreground text-center">Analisando a base…</CardContent></Card>;
  if (!data) return null;

  const c = COLOR[data.color] || COLOR.blue;
  const d = data.distribution;
  const pie = [
    { name: 'Ativos', value: d.active, color: '#10b981' },
    { name: 'Vencendo', value: d.expiring, color: '#f59e0b' },
    { name: 'Vencidos', value: d.expired, color: '#ef4444' },
    { name: 'Suspensos', value: d.suspended, color: '#8b5cf6' },
  ].filter((x) => x.value > 0);

  const gaugeData = [
    { value: data.score, color: c },
    { value: 100 - data.score, color: 'rgba(255,255,255,0.06)' },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HeartPulse className="w-4 h-4" style={{ color: c }} />
          Saúde da base
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 items-center">
          {/* Medidor de nota */}
          <div className="relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={gaugeData} dataKey="value" startAngle={220} endAngle={-40}
                     innerRadius={62} outerRadius={82} paddingAngle={0} stroke="none">
                  {gaugeData.map((g, i) => <Cell key={i} fill={g.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold" style={{ color: c }}>{data.score}</span>
              <span className="text-xs font-semibold" style={{ color: c }}>{data.level}</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">{data.total} clientes</span>
            </div>
          </div>

          {/* Distribuição + recomendações */}
          <div className="space-y-4">
            {/* barrinhas de distribuição */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'Ativos', value: d.active, color: '#10b981' },
                { label: 'Vencendo', value: d.expiring, color: '#f59e0b' },
                { label: 'Vencidos', value: d.expired, color: '#ef4444' },
                { label: 'Suspensos', value: d.suspended, color: '#8b5cf6' },
              ].map((x) => (
                <div key={x.label} className="rounded-lg border border-border/50 p-2.5">
                  <p className="text-lg font-bold" style={{ color: x.color }}>{x.value}</p>
                  <p className="text-[11px] text-muted-foreground">{x.label}</p>
                </div>
              ))}
            </div>

            {/* recomendações */}
            <div className="space-y-2">
              {data.recommendations.map((r, i) => {
                const Icon = REC_ICON[r.icon] || CheckCircle2;
                const tone = r.priority === 'high' ? 'text-red-400 border-red-500/25 bg-red-500/5'
                  : r.priority === 'medium' ? 'text-amber-400 border-amber-500/25 bg-amber-500/5'
                  : 'text-muted-foreground border-border/50 bg-muted/20';
                return (
                  <div key={i} className={`flex items-center gap-2.5 rounded-lg border p-2.5 text-sm ${tone}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{r.text}</span>
                    {(r.icon === 'recover' || r.icon === 'renew' || r.icon === 'risk') && (
                      <button onClick={() => navigate('/customers')} className="text-xs font-medium underline underline-offset-2 shrink-0">
                        Ver
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
